import React from 'react';
import { AlertTriangle, Info, X } from 'lucide-react';
import ModalShell from './ModalShell';
import Button from './Button';

/**
 * Presentational confirm dialog. Prefer useConfirm() from ConfirmContext for
 * promise-based confirms across the app.
 */
const ConfirmModal = ({
  isOpen = false,
  title = 'Are you sure?',
  message = '',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const isDanger = variant === 'danger';
  const Icon = isDanger ? AlertTriangle : Info;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={loading ? undefined : onCancel}
      labelledBy="confirm-modal-title"
      panelClassName="max-w-md"
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-md border border-border bg-surface shadow-sm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        aria-describedby={message ? 'confirm-modal-desc' : undefined}
      >
        <div className="relative p-4 pb-3 sm:p-5 sm:pb-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="absolute right-3 top-3 rounded-md p-1 text-ink-tertiary transition-colors hover:bg-surface-muted hover:text-ink disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="mb-3 flex items-center justify-center">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full border ${
                isDanger
                  ? 'border-red-200 bg-red-50'
                  : 'border-border bg-surface-muted'
              }`}
            >
              <Icon
                className={`h-5 w-5 ${isDanger ? 'text-red-600' : 'text-ink-secondary'}`}
              />
            </div>
          </div>

          <h2
            id="confirm-modal-title"
            className="mb-1 text-center text-base font-semibold text-ink"
          >
            {title}
          </h2>
          {message ? (
            <p
              id="confirm-modal-desc"
              className="text-center text-xs leading-relaxed text-ink-secondary whitespace-pre-line"
            >
              {message}
            </p>
          ) : null}
        </div>

        <div className="flex gap-2 px-4 pb-4 sm:px-5 sm:pb-5">
          <Button
            type="button"
            variant="outline"
            size="lg"
            fullWidth
            className="!rounded-md"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={isDanger ? 'danger' : 'primary'}
            size="lg"
            fullWidth
            className="!rounded-md"
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
};

export default ConfirmModal;
